import React, { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../utils/api'

const AuthContext = createContext(null)

// Module-to-permission mapping for modules that don't map 1:1
const MODULE_MAP = { channels: 'inventory' }

/**
 * Build navigation array from flat permission list.
 * Replaces the server-side getAccessibleNavigation().
 */
function buildNavigation(permissions, isAdmin) {
  const has = (key) => isAdmin || permissions.includes(key)
  const nav = []

  if (has('tickets')) {
    nav.push({ id: 'tickets', name: 'Tickets', path: '/tickets' })
  }

  if (has('deals')) {
    nav.push({
      id: 'deals',
      name: 'Deals',
      path: '/deals',
      children: [
        { id: 'quote-requested', name: 'Quote Requested', path: '/deals/quote-requested' },
        { id: 'quote-expiring', name: 'Quote Expiring', path: '/deals/quote-expiring' },
        { id: 'all-deals', name: 'All Deals', path: '/deals/all' }
      ]
    })
  }

  if (has('inventory')) {
    nav.push({
      id: 'inventory',
      name: 'Inventory',
      path: '/inventory',
      children: [
        { id: 'drafts', name: 'Drafts Manager', path: '/inventory/drafts' },
        { id: 'cleanup', name: 'Inventory Cleanup', path: '/inventory/cleanup' },
        { id: 'inventory-list', name: 'Inventory', path: '/inventory/list' },
        { id: 'repricing', name: 'Repricing', path: '/inventory/repricing' },
        { id: 'channel-listings', name: 'Channel Listings', path: '/inventory/channel-listings' }
      ]
    })
  }

  if (has('orders')) {
    nav.push({ id: 'orders', name: 'Orders', path: '/orders' })
  }

  if (has('warehouse')) {
    nav.push({
      id: 'warehouse',
      name: 'Warehouse',
      path: '/warehouse',
      children: [
        { id: 'receiving', name: 'Receiving', path: '/warehouse/receiving' },
        { id: 'testing', name: 'Testing', path: '/warehouse/testing' },
        { id: 'repairs', name: 'Repairs', path: '/warehouse/repairs' },
        { id: 'issue-trades', name: 'Issue Trades', path: '/warehouse/issue-trades' },
        { id: 'photography', name: 'Photography', path: '/warehouse/photography' },
        { id: 'putaway', name: 'Putaway', path: '/warehouse/putaway' },
        { id: 'collections', name: 'Collections', path: '/warehouse/collections' },
        { id: 'shipping', name: 'Order Shipping', path: '/warehouse/shipping' }
      ]
    })
  }

  if (has('pim')) {
    nav.push({
      id: 'pim',
      name: 'PIM',
      path: '/pim',
      children: [
        { id: 'pim-products', name: 'Products', path: '/pim/products' },
        { id: 'pim-brands', name: 'Brands', path: '/pim/brands' },
        { id: 'pim-categories', name: 'Categories', path: '/pim/categories' },
        { id: 'pim-dimensions', name: 'Dimensions', path: '/pim/dimensions' }
      ]
    })
  }

  if (isAdmin) {
    nav.push({
      id: 'admin',
      name: 'Admin',
      path: '/admin',
      children: [
        { id: 'users', name: 'User Management', path: '/admin/users' }
      ]
    })
  }

  return nav
}

/**
 * Apply user context from a successful auth response.
 */
function applyUserContext(data) {
  const user = data.user || data
  return {
    user: {
      user_id: user.user_id,
      employee_id: user.employee_id,
      name: user.name,
      is_admin: user.is_admin
    },
    permissions: user.permissions || [],
    roles: user.roles || [],
    navigation: buildNavigation(user.permissions || [], user.is_admin)
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [roles, setRoles] = useState([])
  const [navigation, setNavigation] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const response = await api.get('/api/auth/verify')
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
      }
    } catch (err) {
      // Not authenticated — clear state (ProtectedRoute handles redirect)
      setUser(null)
      setPermissions([])
      setRoles([])
      setNavigation([])
    } finally {
      setLoading(false)
    }
  }

  async function login(email, password) {
    setError(null)
    try {
      const response = await api.post('/api/auth/login', { email, password })
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
        return { success: true }
      } else {
        setError(response.error || 'Login failed')
        return { success: false, error: response.error }
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  async function loginEmployee(employeeId, pin) {
    setError(null)
    try {
      const response = await api.post('/api/auth/login/employee', {
        employee_id: employeeId,
        pin
      })
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
        return { success: true }
      } else {
        setError(response.error || 'Login failed')
        return { success: false, error: response.error }
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  async function logout() {
    try {
      await api.delete('/api/auth/session')
    } catch (err) {
      // Ignore logout errors
    }
    setUser(null)
    setPermissions([])
    setRoles([])
    setNavigation([])
  }

  function hasPermission(key) {
    if (!user) return false
    if (user.is_admin) return true
    return permissions.includes(key)
  }

  function hasModule(module) {
    const key = MODULE_MAP[module] || module
    return hasPermission(key)
  }

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      roles,
      navigation,
      loading,
      error,
      login,
      loginEmployee,
      logout,
      hasModule,
      hasPermission,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
